import {
  getCurrentScope,
  watchEffect,
  type MaybeRefOrGetter,
  toValue,
} from "vue";
import { useDebounceFn } from "@vueuse/core";
import {
  CHANNELS,
  useAblyChannelRegistry,
  type MessageReaderHandle,
} from "@/composables/ably/ably-channel-registry";

interface ShowHandles {
  quizHandle: MessageReaderHandle;
  quizUnsub: () => void;
  raffleHandle: MessageReaderHandle;
  raffleUnsub: () => void;
}

function releaseShowHandles(handles: ShowHandles): void {
  handles.quizUnsub();
  handles.quizHandle.release();
  handles.raffleUnsub();
  handles.raffleHandle.release();
}

interface QuizListHandle {
  handle: MessageReaderHandle;
  unsub: () => void;
}

function releaseQuizListHandle(handle: QuizListHandle): void {
  handle.unsub();
  handle.handle.release();
}

interface RaffleListHandle {
  handle: MessageReaderHandle;
  unsub: () => void;
}

function releaseRaffleListHandle(handle: RaffleListHandle): void {
  handle.unsub();
  handle.handle.release();
}

/**
 * Subscribes to the channel-scoped quiz-list and raffle-list Ably channels,
 * and the per-show quiz / raffle channels for every currently-live show on the
 * channel page. When any `quiz-list-changed` / `raffle-list-changed` or
 * contest-state delta arrives, calls `refetchContests` (debounced to ~500 ms
 * to coalesce bursts that the API may publish within milliseconds of each
 * other on a single host action).
 *
 * Plan 078 RCA fix: both quizzes and raffles are CHANNEL-scoped, so the
 * `:quiz-list` and `:raffle-list` channels are keyed by `channelId` — one
 * subscription each for the whole channel page, NOT one per live show. The
 * backend publishes `quiz-list-changed` on `fanfest:{channelId}:quiz-list`
 * and `raffle-list-changed` on `fanfest:{channelId}:raffle-list`; keying
 * these subscriptions by `channelId` aligns them and removes the ambiguous
 * channel->live-show lookup from the publish path.
 *
 * Per-show subscriptions remain for the `:quiz` and `:raffle` channels so
 * in-show state/transition deltas (activateQuizQuestion, endQuizCommand,
 * publishRaffleStep, etc.) still trigger a refetch on the channel page.
 *
 * Subscriptions auto-detach when a show leaves the live-show list (show
 * ended or went to Scheduled). The registry refcount mechanism ensures
 * channels detach when the refcount reaches zero.
 *
 * Call at component setup time. The watchEffects' cleanup handles both
 * reactive re-runs (show list / channel id changes) and component unmount.
 */
export function useChannelContestLiveUpdates(args: {
  /** Reactive getter returning the channel id whose contest lists to watch. */
  channelId: MaybeRefOrGetter<string | null | undefined>;
  /** Reactive getter returning the IDs of currently-live shows on the channel. */
  liveShowIds: MaybeRefOrGetter<string[]>;
  /** Refetch function from the ContestsForChannel query. */
  refetchContests: () => void;
}): void {
  const registry = useAblyChannelRegistry();
  const scope = getCurrentScope() ?? undefined;

  // Debounce to coalesce simultaneous quiz-list + raffle-list deltas that the
  // API publishes within milliseconds of each other on a single host action.
  const debouncedRefetch = useDebounceFn(args.refetchContests, 500);

  // Channel-scoped quiz-list subscription. One handle for the whole channel
  // page (quizzes are channel-scoped). Re-acquired when the channel id changes.
  let activeQuizListHandle: QuizListHandle | null = null;

  watchEffect((onCleanup) => {
    const channelId = toValue(args.channelId);

    if (activeQuizListHandle) {
      releaseQuizListHandle(activeQuizListHandle);
      activeQuizListHandle = null;
    }

    if (channelId) {
      const handle = registry.subscribeChannel({
        descriptor: CHANNELS.quizList,
        channelArgs: [channelId],
        scope,
      });
      // The API publishes quiz-list-changed on the "state" topic (the shared
      // default in ably-realtime.service.ts publishMessage).
      const unsub = handle.subscribe("state", () => {
        void debouncedRefetch();
      });
      activeQuizListHandle = { handle, unsub };
    }

    onCleanup(() => {
      if (activeQuizListHandle) {
        releaseQuizListHandle(activeQuizListHandle);
        activeQuizListHandle = null;
      }
    });
  });

  // Channel-scoped raffle-list subscription. One handle for the whole channel
  // page (raffles are channel-scoped). Re-acquired when the channel id changes.
  let activeRaffleListHandle: RaffleListHandle | null = null;

  watchEffect((onCleanup) => {
    const channelId = toValue(args.channelId);

    if (activeRaffleListHandle) {
      releaseRaffleListHandle(activeRaffleListHandle);
      activeRaffleListHandle = null;
    }

    if (channelId) {
      const handle = registry.subscribeChannel({
        descriptor: CHANNELS.raffleList,
        channelArgs: [channelId],
        scope,
      });
      // The API publishes raffle-list-changed on the "state" topic.
      const unsub = handle.subscribe("state", () => {
        void debouncedRefetch();
      });
      activeRaffleListHandle = { handle, unsub };
    }

    onCleanup(() => {
      if (activeRaffleListHandle) {
        releaseRaffleListHandle(activeRaffleListHandle);
        activeRaffleListHandle = null;
      }
    });
  });

  // Per-showId handle tracking. Populated inside watchEffect; released on re-run
  // (when the live set changes) and on component unmount via onCleanup.
  const activeHandles = new Map<string, ShowHandles>();

  watchEffect((onCleanup) => {
    const nextLiveIds = new Set(toValue(args.liveShowIds));

    // Release handles for shows that are no longer live.
    for (const [showId, handles] of activeHandles) {
      if (!nextLiveIds.has(showId)) {
        releaseShowHandles(handles);
        activeHandles.delete(showId);
      }
    }

    // Acquire handles for newly-live shows.
    for (const showId of nextLiveIds) {
      if (activeHandles.has(showId)) continue;

      // Subscribe to the per-show `:quiz` and `:raffle` channels so
      // contest-side updates that bypass `:quiz-list` / `:raffle-list`
      // also trigger a refetch. The host-UI's StageCommand pipeline
      // (activateQuizQuestion / endQuizQuestion / endQuizCommand,
      // publishRaffleStep / endRaffleCommand) publishes only to `:quiz` /
      // `:raffle` and `:show-state-v2`, not to the list channels — so a
      // channel-page subscriber on list-only would miss the entire
      // question lifecycle and the end-of-quiz / end-of-raffle cascade.
      // This subscription ensures every state/transition the show stage
      // sees also forces a contest refetch on the channel page (debounced).
      const quizHandle = registry.subscribeChannel({
        descriptor: CHANNELS.quiz,
        channelArgs: [showId],
        scope,
      });
      const quizUnsub = quizHandle.subscribe("state", () => {
        void debouncedRefetch();
      });

      const raffleHandle = registry.subscribeChannel({
        descriptor: CHANNELS.raffle,
        channelArgs: [showId],
        scope,
      });
      const raffleUnsub = raffleHandle.subscribe("state", () => {
        void debouncedRefetch();
      });

      activeHandles.set(showId, {
        quizHandle,
        quizUnsub,
        raffleHandle,
        raffleUnsub,
      });
    }

    // On watchEffect re-run or component unmount: release all handles.
    onCleanup(() => {
      for (const [showId, handles] of activeHandles) {
        releaseShowHandles(handles);
        activeHandles.delete(showId);
      }
    });
  });
}
